import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { format, parseISO, differenceInCalendarDays } from "date-fns";
import { fr } from "date-fns/locale";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  CalendarDays,
  ListMusic,
  Lock,
  Music2,
  Loader2,
  Send,
  Sparkles,
  Paperclip,
  Upload,
  Download,
  Trash2,
  Share2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Reveal } from "./Reveal";
import { useAuth } from "@/auth/AuthContext";
import { GoogleLoginButton } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EVENT_TYPES = ["Mariage", "Soirée privée", "Séminaire", "Anniversaire", "Autre"];
const FILTERS = ["Tous", ...EVENT_TYPES];

const StatusChip = ({ verrouillee, days }) =>
  verrouillee ? (
    <span className="flex items-center gap-1.5 rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1 text-[11px] font-bold text-amber-300" data-testid="playlist-status-locked">
      <Lock size={11} /> Verrouillée
    </span>
  ) : (
    <span className="flex items-center gap-1.5 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1 text-[11px] font-bold text-emerald-300" data-testid="playlist-status-open">
      <Sparkles size={11} /> Ouverte{days >= 0 ? ` · J-${days}` : ""}
    </span>
  );

export default function PlaylistsPage() {
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("Tous");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [createForm, setCreateForm] = useState({ titre: "", date: "", type: "Mariage" });
  const [trackForm, setTrackForm] = useState({ titre: "", artiste: "", nom: "" });
  const [guestCode, setGuestCode] = useState("");
  const [guestMode, setGuestMode] = useState(false);
  const [codeError, setCodeError] = useState("");
  const [creating, setCreating] = useState(false);
  const [adding, setAdding] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [categorie, setCategorie] = useState("diaporama");
  const fileInputRef = useRef(null);

  const accessByCode = async (e) => {
    e?.preventDefault();
    if (!guestCode.trim()) return;
    setCodeError("");
    setDetailLoading(true);
    try {
      const { data } = await axios.get(`${API}/playlists/by-code/${guestCode.trim()}`);
      setDetail(data);
      setGuestMode(true);
    } catch (err) {
      setCodeError(err.response?.data?.detail || "Code invalide");
    } finally {
      setDetailLoading(false);
    }
  };

  const copyText = async (text, successMsg, successDesc) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(successMsg, { description: successDesc });
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        toast.success(successMsg, { description: successDesc });
      } catch {
        toast.error("Copie impossible", { description: "Sélectionnez et copiez le texte manuellement." });
      }
      document.body.removeChild(ta);
    }
  };

  const loadFiles = useCallback(async (pid) => {
    try {
      const { data } = await axios.get(`${API}/uploads`, { params: { playlist_id: pid } });
      setFiles(data);
    } catch {}
  }, []);

  const uploadFile = async () => {
    const f = fileInputRef.current?.files?.[0];
    if (!f || !detail) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("playlist_id", detail.playlist.id);
      fd.append("categorie", categorie);
      await axios.post(`${API}/uploads`, fd);
      toast.success("Fichier envoyé", { description: "Il est bien conservé pour votre événement." });
      fileInputRef.current.value = "";
      loadFiles(detail.playlist.id);
    } catch (err) {
      toast.error("Envoi impossible", {
        description: err.response?.data?.detail || "Réessayez avec un autre fichier.",
      });
    } finally {
      setUploading(false);
    }
  };

  const downloadFile = async (f) => {
    try {
      const res = await axios.get(`${API}/files/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = f.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Téléchargement impossible");
    }
  };

  const removeFile = async (f) => {
    try {
      await axios.delete(`${API}/uploads/${f.id}`);
      toast.success("Fichier supprimé");
      if (detail) loadFiles(detail.playlist.id);
    } catch {
      toast.error("Suppression impossible");
    }
  };

  const loadPlaylists = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/playlists`);
      setPlaylists(data);
    } catch {
      toast.error("Impossible de charger les playlists");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPlaylists();
  }, [loadPlaylists]);

  const openDetail = async (id) => {
    setDetailLoading(true);
    try {
      const { data } = await axios.get(`${API}/playlists/${id}`);
      setDetail(data);
      setTrackForm({ titre: "", artiste: "", nom: "" });
      loadFiles(id);
    } catch {
      toast.error("Impossible d'ouvrir cette playlist");
    } finally {
      setDetailLoading(false);
    }
  };

  const createPlaylist = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/playlists`, {
        titre: createForm.titre,
        date_evenement: createForm.date,
        type_evenement: createForm.type,
      });
      toast.success("Playlist créée", { description: "Partagez-la avec vos proches pour recueillir leurs titres." });
      setCreateForm({ titre: "", date: "", type: "Mariage" });
      await loadPlaylists();
      openDetail(data.id);
    } catch {
      toast.error("La création a échoué", { description: "Vérifiez le titre et la date de l'événement." });
    } finally {
      setCreating(false);
    }
  };

  const addTrack = async (e) => {
    e.preventDefault();
    if (!detail) return;
    setAdding(true);
    try {
      const trackUrl = guestMode
        ? `${API}/playlists/by-code/${detail.playlist.code}/tracks`
        : `${API}/playlists/${detail.playlist.id}/tracks`;
      await axios.post(trackUrl, {
        titre: trackForm.titre,
        artiste: trackForm.artiste || null,
        propose_par: trackForm.nom,
      });
      toast.success("Titre ajouté à la playlist");
      setTrackForm({ titre: "", artiste: "", nom: trackForm.nom });
      if (guestMode) {
        const { data } = await axios.get(`${API}/playlists/by-code/${detail.playlist.code}`);
        setDetail(data);
      } else {
        await openDetail(detail.playlist.id);
        loadPlaylists();
      }
    } catch (err) {
      toast.error("Ajout impossible", {
        description: err.response?.data?.detail || "Réessayez dans un instant.",
      });
    } finally {
      setAdding(false);
    }
  };

  const { user, loading: authLoading } = useAuth();
  const visible = playlists.filter((p) => filter === "Tous" || p.type_evenement === filter);

  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#080C14]" data-testid="playlists-auth-loading">
        <Loader2 size={26} className="animate-spin text-cyan-400" />
      </div>
    );
  }

  if (!user && !guestMode) {
    return (
      <div className="min-h-screen bg-[#080C14]" data-testid="playlists-login-gate">
        <header className="border-b border-cyan-500/15 bg-[#080C14]/85 backdrop-blur-xl">
          <div className="mx-auto flex h-[68px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
            <Link
              to="/"
              data-testid="gate-back-link"
              className="group flex items-center gap-2 text-sm font-medium text-slate-300 transition-colors hover:text-cyan-300"
            >
              <ArrowLeft size={16} className="transition-transform duration-300 group-hover:-translate-x-1" />
              Retour au site
            </Link>
            <img src="/logo-v2.png" alt="KLMProd" className="h-8 w-auto" />
          </div>
        </header>
        <div className="mx-auto flex max-w-md flex-col items-center px-6 pt-24 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-cyan-400/25 bg-cyan-400/10">
            <ListMusic size={28} className="text-cyan-300" />
          </div>
          <h1 className="font-display mt-6 text-3xl font-extrabold tracking-tight text-slate-50">
            L'espace playlists <span className="text-gradient">vous attend.</span>
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-slate-400 sm:text-base">
            Mariés ou organisateurs : connectez-vous avec Google pour créer la playlist de votre
            événement et suivre les suggestions de vos proches.
          </p>
          <GoogleLoginButton testId="playlists-login-button" className="mt-8" />

          <div className="mt-8 w-full">
            <div className="flex items-center gap-3 text-xs text-slate-600">
              <span className="h-px flex-1 bg-cyan-500/10" />
              ou
              <span className="h-px flex-1 bg-cyan-500/10" />
            </div>
            <form onSubmit={accessByCode} className="mt-5" data-testid="guest-code-form">
              <p className="text-sm text-slate-400">Vous êtes invité·e ? Entrez votre code d'accès :</p>
              <div className="mt-3 flex gap-2">
                <Input
                  value={guestCode}
                  onChange={(e) => setGuestCode(e.target.value.toUpperCase())}
                  placeholder="EX. A1B2C3"
                  maxLength={8}
                  data-testid="guest-code-input"
                  className="font-mono-brand border-cyan-500/20 bg-[#0E1524]/80 text-center text-lg tracking-[0.3em] text-slate-100 placeholder:text-slate-600 focus-visible:ring-cyan-400/50"
                />
                <button
                  type="submit"
                  data-testid="guest-code-submit"
                  className="bg-brand-gradient shrink-0 rounded-full px-5 text-sm font-bold text-[#04121f] transition-transform duration-300 hover:scale-105"
                >
                  Accéder
                </button>
              </div>
              {codeError && (
                <p className="mt-2 text-xs text-red-400" data-testid="guest-code-error">{codeError}</p>
              )}
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080C14]" data-testid="playlists-page">
      <header className="sticky top-0 z-40 border-b border-cyan-500/15 bg-[#080C14]/85 backdrop-blur-xl">
        <div className="mx-auto flex h-[68px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/" data-testid="playlists-back-link" className="group flex items-center gap-2 text-sm font-medium text-slate-300 transition-colors hover:text-cyan-300">
            <ArrowLeft size={16} className="transition-transform duration-300 group-hover:-translate-x-1" />
            Retour au site
          </Link>
          <img src="/logo-v2.png" alt="KLMProd" className="h-8 w-auto" />
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <Reveal>
          <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-cyan-400">Espace playlists collaboratives</p>
          <h1 className="font-display mt-4 max-w-3xl text-4xl font-extrabold tracking-tight text-slate-50 sm:text-5xl">
            Une playlist par événement, <span className="text-gradient">alimentée par vos proches.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-400">
            Retrouvez ici les playlists classées par date et par prestation. Ajoutez vos titres tant
            que la playlist est ouverte — les demandes se clôturent 48 h avant l'événement.
          </p>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className={guestMode ? "hidden lg:hidden" : "space-y-8 lg:col-span-5"}>
            <Reveal>
              <form onSubmit={createPlaylist} className="rounded-3xl border border-cyan-500/15 bg-[#0E1524]/80 p-6 backdrop-blur-xl sm:p-7" data-testid="playlist-create-form">
                <h2 className="font-display text-lg font-bold text-slate-100">Créer une playlist</h2>
                <div className="mt-5 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="pl-titre" className="text-sm text-slate-300">Nom de l'événement *</Label>
                    <Input
                      id="pl-titre"
                      data-testid="playlist-titre-input"
                      required
                      value={createForm.titre}
                      onChange={(e) => setCreateForm({ ...createForm, titre: e.target.value })}
                      placeholder="Mariage Camille & Alex"
                      className="border-cyan-500/20 bg-[#080C14]/70 text-slate-100 placeholder:text-slate-600 focus-visible:ring-cyan-400/50"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="pl-date" className="text-sm text-slate-300">Date *</Label>
                      <Input
                        id="pl-date"
                        data-testid="playlist-date-input"
                        type="date"
                        required
                        value={createForm.date}
                        onChange={(e) => setCreateForm({ ...createForm, date: e.target.value })}
                        className="border-cyan-500/20 bg-[#080C14]/70 text-slate-100 focus-visible:ring-cyan-400/50"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm text-slate-300">Prestation</Label>
                      <Select value={createForm.type} onValueChange={(v) => setCreateForm({ ...createForm, type: v })}>
                        <SelectTrigger data-testid="playlist-type-select" className="border-cyan-500/20 bg-[#080C14]/70 text-slate-100 focus:ring-cyan-400/50">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="border-cyan-500/20 bg-[#0E1524] text-slate-100">
                          {EVENT_TYPES.map((t) => (
                            <SelectItem key={t} value={t} data-testid={`playlist-type-option-${t.toLowerCase().replace(/[^a-z]/g, "-")}`}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <button
                    type="submit"
                    data-testid="playlist-create-button"
                    disabled={creating}
                    className="bg-brand-gradient flex w-full items-center justify-center gap-2 rounded-full py-3 text-sm font-bold text-[#04121f] transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
                  >
                    {creating ? <Loader2 size={16} className="animate-spin" /> : <ListMusic size={16} />}
                    {creating ? "Création…" : "Créer la playlist"}
                  </button>
                </div>
              </form>
            </Reveal>

            <div>
              <div className="flex flex-wrap gap-2" data-testid="playlist-filters">
                {FILTERS.map((f) => (
                  <button
                    key={f}
                    data-testid={`playlist-filter-${f.toLowerCase().replace(/[^a-z]/g, "-")}`}
                    onClick={() => setFilter(f)}
                    className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors duration-300 ${
                      filter === f
                        ? "bg-brand-gradient text-[#04121f]"
                        : "border border-cyan-500/20 text-slate-400 hover:border-cyan-400/50 hover:text-cyan-300"
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>

              <div className="mt-5 space-y-3" data-testid="playlist-list">
                {loading && <p className="py-8 text-sm text-slate-500">Chargement des playlists…</p>}
                {!loading && visible.length === 0 && (
                  <p className="rounded-2xl border border-cyan-500/10 bg-[#0E1524]/60 p-6 text-sm text-slate-500" data-testid="playlist-empty-state">
                    Aucune playlist pour le moment. Créez la première pour votre événement.
                  </p>
                )}
                {visible.map((p) => {
                  const days = differenceInCalendarDays(parseISO(p.date_evenement), new Date());
                  const active = detail?.playlist.id === p.id;
                  return (
                    <button
                      key={p.id}
                      data-testid={`playlist-card-${p.id}`}
                      onClick={() => openDetail(p.id)}
                      className={`flex w-full items-center gap-4 rounded-2xl border p-4 text-left transition-all duration-300 ${
                        active
                          ? "border-cyan-400/50 bg-cyan-400/10"
                          : "border-cyan-500/15 bg-[#0E1524]/70 hover:border-cyan-400/40"
                      }`}
                    >
                      <div className="flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded-xl border border-cyan-400/20 bg-[#080C14]">
                        <span className="font-display text-lg font-black leading-none text-cyan-300">
                          {format(parseISO(p.date_evenement), "d")}
                        </span>
                        <span className="mt-0.5 text-[10px] uppercase tracking-wider text-slate-500">
                          {format(parseISO(p.date_evenement), "MMM yy", { locale: fr })}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-bold text-slate-100">{p.titre}</p>
                        <p className="mt-0.5 flex items-center gap-2 text-xs text-slate-500">
                          <span className="text-cyan-400/80">{p.type_evenement}</span>·
                          <Music2 size={11} /> {p.tracks_count} titre{p.tracks_count > 1 ? "s" : ""}
                        </p>
                      </div>
                      <StatusChip verrouillee={p.verrouillee} days={days} />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className={guestMode ? "lg:col-span-12" : "lg:col-span-7"}>
            <Reveal delay={0.1}>
              <div className="min-h-[420px] rounded-3xl border border-cyan-500/15 bg-[#0E1524]/80 p-6 backdrop-blur-xl sm:p-8" data-testid="playlist-detail-panel">
                {detailLoading && (
                  <div className="flex h-[380px] items-center justify-center">
                    <Loader2 size={26} className="animate-spin text-cyan-400" />
                  </div>
                )}

                {!detailLoading && !detail && (
                  <div className="flex h-[380px] flex-col items-center justify-center text-center" data-testid="playlist-detail-empty">
                    <ListMusic size={40} className="text-cyan-400/50" />
                    <p className="mt-4 max-w-xs text-sm leading-relaxed text-slate-500">
                      Sélectionnez une playlist pour voir les titres proposés et ajouter les vôtres.
                    </p>
                  </div>
                )}

                {!detailLoading && detail && (
                  <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <h2 className="font-display text-2xl font-extrabold text-slate-50" data-testid="playlist-detail-title">
                          {detail.playlist.titre}
                        </h2>
                        <p className="mt-1.5 flex items-center gap-2 text-sm text-slate-400">
                          <CalendarDays size={14} className="text-cyan-400" />
                          {format(parseISO(detail.playlist.date_evenement), "EEEE d MMMM yyyy", { locale: fr })}
                          <span className="text-cyan-400/80">· {detail.playlist.type_evenement}</span>
                        </p>
                      </div>
                      <StatusChip
                        verrouillee={detail.verrouillee}
                        days={differenceInCalendarDays(parseISO(detail.playlist.date_evenement), new Date())}
                      />
                      {detail.playlist.code && !guestMode && (
                        <button
                          data-testid="playlist-code-chip"
                          onClick={() => copyText(detail.playlist.code, "Code copié", "Partagez-le avec vos invités.")}
                          title="Copier le code invités"
                          className="flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-3.5 py-1 text-[11px] font-bold text-cyan-300 transition-colors hover:bg-cyan-400/20"
                        >
                          Code invités : <span className="font-mono-brand tracking-widest">{detail.playlist.code}</span>
                        </button>
                      )}
                      {detail.playlist.code && !guestMode && (
                        <button
                          data-testid="playlist-share-button"
                          onClick={() => {
                            const msg = `${detail.playlist.titre} — DJ KLMProd\n\nProposez vos titres pour la soirée ! Rendez-vous sur ${window.location.origin}/playlists, rubrique « code d'accès », et entrez le code : ${detail.playlist.code}\n\nVous avez jusqu'à 48 h avant l'événement. À très vite sur la piste de danse !`;
                            copyText(msg, "Message copié", "Collez-le dans votre SMS, WhatsApp ou e-mail aux invités.");
                          }}
                          title="Copier le message à envoyer aux invités"
                          className="flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3.5 py-1 text-[11px] font-bold text-emerald-300 transition-colors hover:bg-emerald-400/20"
                        >
                          <Share2 size={11} /> Message invités
                        </button>
                      )}
                    </div>

                    <div className="my-6 h-px bg-gradient-to-r from-transparent via-cyan-400/25 to-transparent" />

                    <div className="max-h-[300px] space-y-2 overflow-y-auto pr-1" data-testid="playlist-track-list">
                      {detail.tracks.length === 0 && (
                        <p className="py-6 text-center text-sm text-slate-500" data-testid="playlist-tracks-empty">
                          Aucun titre pour l'instant — soyez le premier à en proposer un.
                        </p>
                      )}
                      {detail.tracks.map((t, i) => (
                        <div key={t.id} className="flex items-center gap-3 rounded-xl border border-cyan-500/10 bg-[#080C14]/60 px-4 py-3" data-testid={`playlist-track-${i}`}>
                          <span className="font-mono-brand w-6 text-xs text-cyan-400/70">{String(i + 1).padStart(2, "0")}</span>
                          <Music2 size={15} className="shrink-0 text-cyan-400" />
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-semibold text-slate-100">
                              {t.titre}
                              {t.artiste && <span className="font-normal text-slate-400"> — {t.artiste}</span>}
                            </p>
                          </div>
                          <span className="shrink-0 text-xs text-slate-500">par {t.propose_par}</span>
                        </div>
                      ))}
                    </div>

                    {detail.verrouillee ? (
                      <div className="mt-6 flex items-start gap-3 rounded-2xl border border-amber-400/25 bg-amber-400/5 p-4" data-testid="playlist-locked-notice">
                        <Lock size={18} className="mt-0.5 shrink-0 text-amber-300" />
                        <p className="text-sm leading-relaxed text-slate-300">
                          Les demandes sont clôturées 48 h avant l'événement. La programmation est
                          désormais verrouillée pour garantir des transitions fluides le jour J.
                        </p>
                      </div>
                    ) : (
                      <form onSubmit={addTrack} className="mt-6 space-y-4" data-testid="track-add-form">
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                          <Input
                            data-testid="track-titre-input"
                            required
                            value={trackForm.titre}
                            onChange={(e) => setTrackForm({ ...trackForm, titre: e.target.value })}
                            placeholder="Titre du morceau *"
                            className="border-cyan-500/20 bg-[#080C14]/70 text-slate-100 placeholder:text-slate-600 focus-visible:ring-cyan-400/50"
                          />
                          <Input
                            data-testid="track-artiste-input"
                            value={trackForm.artiste}
                            onChange={(e) => setTrackForm({ ...trackForm, artiste: e.target.value })}
                            placeholder="Artiste"
                            className="border-cyan-500/20 bg-[#080C14]/70 text-slate-100 placeholder:text-slate-600 focus-visible:ring-cyan-400/50"
                          />
                          <Input
                            data-testid="track-nom-input"
                            required
                            value={trackForm.nom}
                            onChange={(e) => setTrackForm({ ...trackForm, nom: e.target.value })}
                            placeholder="Votre prénom *"
                            className="border-cyan-500/20 bg-[#080C14]/70 text-slate-100 placeholder:text-slate-600 focus-visible:ring-cyan-400/50"
                          />
                        </div>
                        <button
                          type="submit"
                          data-testid="track-submit-button"
                          disabled={adding}
                          className="bg-brand-gradient flex w-full items-center justify-center gap-2 rounded-full py-3 text-sm font-bold text-[#04121f] transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
                        >
                          {adding ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />}
                          {adding ? "Ajout…" : "Proposer ce titre"}
                        </button>
                      </form>
                    )}

                    <div className="my-6 h-px bg-gradient-to-r from-transparent via-cyan-400/25 to-transparent" />

                    {!guestMode && (
                    <div data-testid="upload-section">
                      <h3 className="font-display flex items-center gap-2 text-base font-bold text-slate-100">
                        <Paperclip size={16} className="text-cyan-400" />
                        Vos fichiers pour le jour J
                      </h3>
                      <p className="mt-1.5 text-xs leading-relaxed text-slate-500">
                        Diaporamas, photos, musiques de discours… déposez vos éléments ici, au plus
                        tard 48 h avant l'événement (50 Mo max par fichier).
                      </p>
                      <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                        <Select value={categorie} onValueChange={setCategorie}>
                          <SelectTrigger
                            data-testid="upload-categorie-select"
                            className="border-cyan-500/20 bg-[#080C14]/70 text-slate-100 focus:ring-cyan-400/50 sm:w-44"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="border-cyan-500/20 bg-[#0E1524] text-slate-100">
                            <SelectItem value="diaporama" data-testid="upload-cat-diaporama">Diaporama</SelectItem>
                            <SelectItem value="photo" data-testid="upload-cat-photo">Photo</SelectItem>
                            <SelectItem value="audio" data-testid="upload-cat-audio">Musique / audio</SelectItem>
                            <SelectItem value="document" data-testid="upload-cat-document">Document</SelectItem>
                            <SelectItem value="autre" data-testid="upload-cat-autre">Autre</SelectItem>
                          </SelectContent>
                        </Select>
                        <input
                          ref={fileInputRef}
                          type="file"
                          data-testid="upload-file-input"
                          className="flex-1 self-center text-xs text-slate-400 file:mr-3 file:rounded-full file:border-0 file:bg-cyan-400/10 file:px-4 file:py-2 file:text-xs file:font-semibold file:text-cyan-300 hover:file:bg-cyan-400/20"
                        />
                        <button
                          onClick={uploadFile}
                          disabled={uploading}
                          data-testid="upload-submit-button"
                          className="bg-brand-gradient flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-xs font-bold text-[#04121f] transition-transform duration-300 hover:scale-[1.03] disabled:opacity-60"
                        >
                          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                          Envoyer
                        </button>
                      </div>
                      <div className="mt-4 space-y-2" data-testid="upload-file-list">
                        {files.length === 0 && (
                          <p className="text-xs text-slate-600">Aucun fichier envoyé pour cet événement.</p>
                        )}
                        {files.map((f) => (
                          <div
                            key={f.id}
                            className="flex items-center gap-3 rounded-xl border border-cyan-500/10 bg-[#080C14]/60 px-4 py-2.5"
                            data-testid={`upload-file-${f.id}`}
                          >
                            <Paperclip size={13} className="shrink-0 text-cyan-400" />
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-xs font-semibold text-slate-200">{f.original_filename}</p>
                              <p className="text-[10px] capitalize text-slate-500">
                                {f.categorie} · {(f.size / 1024 / 1024).toFixed(1)} Mo
                              </p>
                            </div>
                            <button
                              onClick={() => downloadFile(f)}
                              data-testid={`upload-download-${f.id}`}
                              className="text-slate-400 transition-colors hover:text-cyan-300"
                              aria-label="Télécharger"
                            >
                              <Download size={14} />
                            </button>
                            <button
                              onClick={() => removeFile(f)}
                              data-testid={`upload-delete-${f.id}`}
                              className="text-slate-400 transition-colors hover:text-red-300"
                              aria-label="Supprimer"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                    )}
                  </motion.div>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </div>
  );
}
